import { splitOrder, validateCart, ValidateCartApiParameter } from "@bevco/Services/CustomCartValidationService";
import { resolveFormAddressForValidation } from "@bevco/Store/Handlers/CheckoutShipping/ResolveGuestCheckoutAddress";
import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { BaseAddressModel, CountryModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    apiParameter: ValidateCartApiParameter;
    onSuccess?: () => void;
}

type HandlerType = ApiHandlerDiscreteParameter<Parameter, ValidateCartApiParameter, any>;

const resolveStateAbbreviation = (
    address: BaseAddressModel | undefined,
    countries: CountryModel[] | undefined,
): string => {
    if (!address?.state) {
        return "";
    }

    if (address.state.abbreviation) {
        return address.state.abbreviation;
    }

    if (address.state.id && countries) {
        const country = address.country?.id
            ? countries.find(c => c.id === address.country!.id)
            : countries[0];
        return country?.states?.find(s => s.id === address.state!.id)?.abbreviation ?? "";
    }

    return "";
};

const enrichValidateCartParameter: HandlerType = props => {
    const state = props.getState();
    const { useBillingAddress, shippingAddressFormState, billingAddressFormState, cartId } =
        state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const countries = getCurrentCountries(state);
    const shipToFromStore = getShipToState(state, cart?.shipToId).value;
    const isGuest = getCurrentUserIsGuest(state);

    const formAddress = resolveFormAddressForValidation(
        isGuest,
        useBillingAddress,
        billingAddressFormState?.address,
        shippingAddressFormState?.address,
        shipToFromStore,
    );

    const resolvedRegion = resolveStateAbbreviation(formAddress, countries);

    props.apiParameter = {
        ...props.parameter.apiParameter,
        shipToRegion: resolvedRegion,
        shipToCustNbr:
            formAddress?.customerSequence ||
            formAddress?.customerNumber ||
            shipToFromStore?.customerSequence ||
            shipToFromStore?.customerNumber ||
            props.parameter.apiParameter.shipToCustNbr ||
            "",
    };
};

const validateCartHandler: HandlerType = async props => {
    props.apiResult = await validateCart(props.apiParameter);
};

const dispatchSetRedirect: HandlerType = props => {
    if (props.apiResult.results && props.apiResult.results.length > 0) {
        props.dispatch({
            type: "Custom/CheckoutShipping/RedirectToCartPage",
            redirectToCartPage: true,
        } as any);
    }
};

const dispatchValidationResults: HandlerType = props => {
    props.dispatch({
        type: "Custom/CheckoutShipping/CartLines",
        cartLines: props.apiResult.results,
    } as any);
};

const dispatchValidateSplitOrder: HandlerType = async props => {
    const results = await splitOrder({
        t: Date.now().toString(),
    });

    if (results) {
        // eslint-disable-next-line no-console
        console.log("Split order results: ", results);
    }
};

const reloadCartAfterValidation: HandlerType = async props => {
    // IEIXOP-7020: validateCart persists optionDescription server-side; Review reads Redux cart.
    // Without a reload, Review keeps the pre-validation cart and the shipment heading stays blank.
    if (props.apiResult?.results?.length > 0) {
        return;
    }

    const state = props.getState();
    const { cartId } = state.pages.checkoutShipping;

    if (cartId) {
        const awaitableLoadCart = makeHandlerChainAwaitable(loadCart);
        await awaitableLoadCart({ cartId, getPromotions: true, replaceCart: true } as any)(
            props.dispatch,
            props.getState,
        );
        return;
    }

    const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
    await awaitableLoadCurrentCart({ shouldLoadFullCart: true, replaceCart: true } as any)(
        props.dispatch,
        props.getState,
    );
};

const chain = [
    enrichValidateCartParameter,
    validateCartHandler,
    dispatchSetRedirect,
    dispatchValidationResults,
    dispatchValidateSplitOrder,
    reloadCartAfterValidation,
];

const customValidateCart = createHandlerChainRunner(chain, "CustomValidateCart");
export default customValidateCart;
